import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Runs an async function and exposes explicit states: 'loading' | 'success' | 'error'.
 * Stale responses (changed deps, unmount) are ignored. `reload()` re-runs it.
 */
const useAsync = (fn, deps) => {
    const [state, setState] = useState({ status: 'loading', data: undefined, error: undefined });
    const [tick, setTick] = useState(0);
    const fnRef = useRef(fn);
    fnRef.current = fn;

    useEffect(() => {
        let cancelled = false;
        setState(prev => ({ status: 'loading', data: prev.data, error: undefined }));
        Promise.resolve()
            .then(() => fnRef.current())
            .then(
                data => !cancelled && setState({ status: 'success', data, error: undefined }),
                error => !cancelled && setState({ status: 'error', data: undefined, error })
            );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, tick]);

    const reload = useCallback(() => {
        setState(prev => ({ ...prev, status: 'loading' }));
        setTick(t => t + 1);
    }, []);

    return { ...state, reload };
};

export default useAsync;
